import { groupBy, sortBy } from 'lodash-es'
import {
    ItemCategoryLiteContract,
    ItemType,
    ProductContract,
    UOMContract,
} from 'models'
import { useCallback } from 'react'
import { useState, useMemo, useEffect } from 'react'
import { CreateProductHeader } from '../../productTable/CreateProductHeader'
import nameof from 'utilities/nameof'
import { DEFAULT_BRAND } from 'components/BrandFilter/useBrandFtiler'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import useFetchApi, { isLoading } from 'hooks/useFetchApi/useFetchApi'
import useLocationBrands from 'components/OperationalSetting/Brands/useLocationBrands'
import productService from 'Services/api/product.service'
import { fetchWithNotification } from 'store/actions/base.action'
import productCategoryService from 'Services/api/productCategory.service'
import uomService from 'Services/api/uom.service'
import toast from 'utilities/toast/toast'
import useProductErrorHandler from 'views/Products/hooks/useProductErrorHandler'
import locationService from 'Services/api/location.service'

const PRODUCT_FIELD = nameof<ProductContract>()

export interface IProductSlim {
    itemId: string
    itemName: string
}
interface EditableProductModel
    extends Omit<ProductContract, 'hasLocationData'> {
    hasLocationData?: boolean
}
interface IDependencyData {
    products: EditableProductModel[]
    uoms: UOMContract[]
    categories: ItemCategoryLiteContract[]
    companyTaxInclusiveSetting: boolean
}

const defaultDependency: IDependencyData = {
    products: [],
    uoms: [],
    categories: [],
    companyTaxInclusiveSetting: false,
}

export default function useProductMain() {
    const { brands: locationBrands, fetchBrandsDispatch } = useLocationBrands()
    const { isMobile } = useScreenSize()
    const [loadingDependency, setLoadingDependency] = useState(false)

    const [dependencies, setDependencies] = useState<IDependencyData>(
        defaultDependency,
    )

    const [, updateFlag, updateFlagStatus] = useFetchApi(
        productService.updateFlag,
    )
    const [, removeProducts, removeProductsStatus] = useFetchApi(
        productService.deleteProducts,
    )

    const updateAllowLocationData = useCallback((itemId: string) => {
        setDependencies((prevs) => ({
            ...prevs,
            products: prevs.products.map((p) => {
                if (p.itemID === itemId) p.hasLocationData = true
                return p
            }),
        }))
    }, [])

    const locationProductBrandNames = useMemo(() => {
        const locationProducts = dependencies.products
            .filter((a) => a.type !== ItemType.Combo)
            .filter(
                (i) =>
                    !i.brandID ||
                    locationBrands
                        .filter((b) => b.isActive)
                        .map((b) => b.brandID)
                        .includes(i.brandID),
            )

        return Object.keys(
            groupBy(locationProducts, (a) => a.brandName || DEFAULT_BRAND),
        )
    }, [locationBrands, dependencies.products])

    const getProductAndDependency = useCallback(async () => {
        try {
            setLoadingDependency(true)

            const results = await fetchWithNotification({
                apiLinkFnc: () =>
                    Promise.all([
                        productCategoryService.getProductCategoryLookupList(),
                        uomService.getAllUOMs(),
                        productService.getProducts(true),
                        locationService.getLocationSettingOfCurrentUser(),
                    ]),
            })
            setDependencies({
                categories: results[0].data,
                uoms: results[1].data,
                products: results[2].data,
                companyTaxInclusiveSetting:
                    results[3].data.companyProfile?.isTaxInclusive ?? false,
            })
        } catch (error) {
            // We may not need this after future task POS-6890
            // ...
        } finally {
            setLoadingDependency(false)
        }
    }, [])

    const updateCachedProductFlags = useCallback(
        (updatingProductId: string, flags: string[], newValue: boolean) => {
            setDependencies((prev) => ({
                ...prev,
                products: prev.products.map((p) => {
                    if (p.itemID === updatingProductId) {
                        const newProduct = { ...p }
                        flags.forEach((flagKeyName) => {
                            ;(newProduct as any)[flagKeyName] = newValue
                        })
                        return newProduct
                    } else return p
                }),
            }))
        },
        [],
    )
    const { validationError } = useProductErrorHandler()
    const handleOnToggleSwitch = useCallback(
        async (
            key: string,
            toggleItem: ProductContract,
            isForce: boolean = false,
        ) => {
            if (isLoading(updateFlagStatus)) return
            const updateSection: string[] = [
                PRODUCT_FIELD.isAvailableForSelection,
            ]
            const id = toggleItem.itemID!!
            const newValue: boolean = !(toggleItem as any)[key]
            try {
                // TODO: Implement rate control e.g. debounce to prevent spamming status update.
                if (key === PRODUCT_FIELD.isActive) {
                    updateSection.push(PRODUCT_FIELD.isActive)
                    updateCachedProductFlags(id, updateSection, newValue)
                    await updateFlag(
                        id,
                        PRODUCT_FIELD.isActive,
                        newValue,
                        isForce,
                    )
                } else if (key === PRODUCT_FIELD.isAvailableForSelection) {
                    updateCachedProductFlags(id, updateSection, newValue)
                    await updateFlag(
                        id,
                        PRODUCT_FIELD.isAvailableForSelection,
                        newValue,
                    )
                }
                toast.success(
                    `<strong>${
                        toggleItem.displayName ||
                        toggleItem.name ||
                        'The product'
                    }</strong> was successfully ${
                        newValue ? 'activated' : 'deactived'
                    }.`,
                )
            } catch (err) {
                validationError(toggleItem.name!, err, {
                    onConfirmForceCombo: (result) => {
                        if (result)
                            handleOnToggleSwitch(key, toggleItem ?? {}, true)
                        else
                            updateCachedProductFlags(
                                id,
                                updateSection,
                                !newValue,
                            )
                    },
                })
            }
        },
        [
            updateCachedProductFlags,
            updateFlag,
            updateFlagStatus,
            validationError,
        ],
    )

    const handleOnDelete = useCallback(
        async (selected: ProductContract[]) => {
            const removingIds = selected.map((p) => p.itemID!)
            await removeProducts(removingIds).then(() => {
                setDependencies((prev) => ({
                    ...prev,
                    products: prev.products.filter(
                        (p) => !removingIds.includes(p.itemID!),
                    ),
                }))
                toast.success(
                    `The ${
                        removingIds.length > 1 ? 'items were' : 'item was'
                    } successfully removed.`,
                )
            })
        },
        [removeProducts],
    )

    const headers = useMemo(
        () =>
            CreateProductHeader(
                dependencies.categories,
                dependencies.uoms,
                locationBrands.length > 0,
                isMobile,
                false,
            ),
        [
            dependencies.categories,
            dependencies.uoms,
            isMobile,
            locationBrands.length,
        ],
    )

    const [sortedProducts, setSortedProducts] = useState<ProductContract[]>([])

    const displayProducts = useMemo(() => {
        if (sortedProducts.length === 0)
            return sortBy(dependencies.products, (i) => i.name).map<
                IProductSlim
            >(
                (i) => ({
                    itemId: i.itemID!,
                    itemName: i.name!,
                }),
                [],
            )
        return sortedProducts.map<IProductSlim>((i) => ({
            itemId: i.itemID!,
            itemName: i.name!,
        }))
    }, [dependencies.products, sortedProducts])

    const onSyncSortedProducts = useCallback(
        (tableOrders: ProductContract[]) => {
            setSortedProducts(tableOrders)
        },
        [],
    )

    useEffect(() => {
        fetchBrandsDispatch()
        getProductAndDependency()
    }, [fetchBrandsDispatch, getProductAndDependency])

    const updateProductNameInDisplayProduct = useCallback(
        (updatingProductId: string, newProductName: string) => {
            setDependencies((prev) => {
                return {
                    ...prev,
                    products: prev.products.map((m) => {
                        if (m.itemID === updatingProductId) {
                            return {
                                ...m,
                                name: newProductName,
                            }
                        }
                        return m
                    }),
                }
            })
            setSortedProducts((prev) => {
                return prev.map((m) => {
                    if (m.itemID === updatingProductId) {
                        return { ...m, name: newProductName }
                    }
                    return m
                })
            })
        },
        [],
    )

    return {
        headers,
        dependencies,
        locationProductBrandNames,
        handleOnDelete,
        handleOnToggleSwitch,
        reload: getProductAndDependency,
        loading: loadingDependency,
        deleting: isLoading(removeProductsStatus),
        updateAllowLocationData,
        displayProducts,
        onSyncSortedProducts,
        updateProductNameInDisplayProduct,
    }
}

export type useProductMainType = ReturnType<typeof useProductMain>
